// Opening the Django integrations-sync page for whoever is currently selected.
// Both athlete selectors (the navbar chip and the sidebar name) offer it, so the
// session bridge and the target-id rules live here rather than in either one.

import { apiClient } from '@/services/api/client'
import { getDjangoOrigin } from '@/lib/djangoOrigin'

export interface SyncTarget {
  /** Django user id of the selected athlete, when one is selected. */
  userId?: number | null
  /** Profile id fallback, for a selection that carries no user id. */
  profileId?: number | null
}

/** Django identifies the athlete by user id where it has one, and only falls
 *  back to the profile id. Neither means the coach's own data. */
export function buildIntegrationsSyncUrl({ userId, profileId }: SyncTarget): string {
  const url = new URL(`${getDjangoOrigin()}/auth_account/processing-preferences/`, window.location.origin)
  url.searchParams.set('source', 'webapp')
  if (userId) {
    url.searchParams.set('target_user_id', String(userId))
  } else if (profileId) {
    url.searchParams.set('target_profile_id', String(profileId))
  }
  return url.toString()
}

/** Bridge the webapp session into Django, then open the sync page in a new tab.
 *  A failed bridge still opens the page: the user lands on a login screen there,
 *  which is a better outcome than a button that silently does nothing. */
export async function openIntegrationsSync(target: SyncTarget): Promise<void> {
  try {
    await apiClient.post('/auth_account/session-bridge/')
  } catch {
    // Bridge failed -- open anyway, user will see login page.
  }
  // noopener: the opened page gets no `window.opener` handle back to this
  // authenticated tab. noreferrer: the current webapp URL does not ride along.
  window.open(buildIntegrationsSyncUrl(target), '_blank', 'noopener,noreferrer')
}
